import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, CircularProgress, Alert,
  Button, Tooltip, Divider, LinearProgress, IconButton, Switch,
  FormControlLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, alpha, useTheme, Collapse,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TimelineIcon from '@mui/icons-material/Timeline';
import ScienceIcon from '@mui/icons-material/Science';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SecurityIcon from '@mui/icons-material/Security';
import HistoryIcon from '@mui/icons-material/History';

const STRATEGY_ENGINE_URL =
  process.env.REACT_APP_STRATEGY_ENGINE_URL ||
  'https://strategy-engine.houman-ghavamzadeh.workers.dev';

const headers = () => ({
  'X-User-Id': localStorage.getItem('user_id') || 'default-user',
  'Content-Type': 'application/json',
});

// ============================================================================
// HELPER: format PnL with color
// ============================================================================
function PnlChip({ value, suffix = '%' }) {
  if (value === null || value === undefined) return <Chip label="—" size="small" variant="outlined" />;
  const isPositive = value > 0;
  return (
    <Chip
      label={`${isPositive ? '+' : ''}${Number(value).toFixed(2)}${suffix}`}
      size="small"
      sx={{
        fontSize: '0.7rem',
        fontWeight: 'bold',
        bgcolor: isPositive ? 'success.main' : value < 0 ? 'error.main' : 'grey.500',
        color: '#fff',
        height: 22,
      }}
    />
  );
}

// ============================================================================
// AI CONFIG STATUS CARD
// ============================================================================
function AIConfigCard({ config, onRunDaily, running }) {
  const theme = useTheme();
  if (!config) return null;

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: config.aiModeEnabled ? 'primary.main' : 'divider', borderRadius: 2, bgcolor: config.aiModeEnabled ? alpha(theme.palette.primary.main, 0.02) : 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SmartToyIcon sx={{ fontSize: 20, color: config.aiModeEnabled ? 'primary.main' : 'text.disabled' }} />
          <Typography variant="subtitle2" fontWeight="bold">AI Schedule</Typography>
          <Chip
            label={config.aiModeEnabled ? 'Active' : 'Off'}
            size="small"
            color={config.aiModeEnabled ? 'primary' : 'default'}
            variant={config.aiModeEnabled ? 'filled' : 'outlined'}
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
          {config.experimentEnabled && (
            <Chip label="Experiment Mode" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
          )}
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={running ? <CircularProgress size={14} /> : <PlayArrowIcon />}
          onClick={onRunDaily}
          disabled={running || !config.aiModeEnabled}
          sx={{ textTransform: 'none', fontSize: '0.75rem' }}
        >
          Run Daily Update
        </Button>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" color="text.secondary">Rotation Policy</Typography>
          <Typography variant="body2" fontWeight="bold">{config.rotationPolicy === 'round_robin' ? 'Round Robin' : 'Bandit'}</Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" color="text.secondary">Allowed Modes</Typography>
          <Typography variant="body2" fontWeight="bold">{config.allowedModeIds?.length || 0} modes</Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" color="text.secondary">Current Mode</Typography>
          <Typography variant="body2" fontWeight="bold" sx={{ color: 'primary.main' }}>
            {config.currentMode || 'None'}
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );
}

// ============================================================================
// CURRENT POLICY PLAN CARD
// ============================================================================
function PolicyPlanCard({ plan }) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  if (!plan) {
    return (
      <Paper elevation={0} sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
        <AutoFixHighIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">No policy plan generated yet. Run a daily update to generate one.</Typography>
      </Paper>
    );
  }

  const regimeColor = plan.regime?.label === 'trending' ? 'success' : plan.regime?.label === 'choppy' ? 'error' : 'warning';

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoFixHighIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          <Typography variant="subtitle2" fontWeight="bold">Current Policy Plan</Typography>
          <Chip label={`v${plan.version}`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
        </Box>
        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary">Mode</Typography>
          <Typography variant="body2" fontWeight="bold">{plan.selectedModeId}</Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary">Intent</Typography>
          <Chip label={plan.intent} size="small" color="info" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary">Regime</Typography>
          <Chip label={plan.regime?.label || 'unknown'} size="small" color={regimeColor} sx={{ fontSize: '0.65rem', height: 20 }} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary">Generated</Typography>
          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
            {plan.generatedAt ? new Date(plan.generatedAt).toLocaleString() : '—'}
          </Typography>
        </Grid>
      </Grid>

      <Collapse in={expanded}>
        <Divider sx={{ my: 1.5 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">Entry Policy</Typography>
            <Box sx={{ mt: 0.5, fontSize: '0.75rem' }}>
              {plan.entryPolicy?.confidenceThresholdOverride && (
                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Confidence: {plan.entryPolicy.confidenceThresholdOverride}%</Typography>
              )}
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Strict Entry: {plan.entryPolicy?.strictEntryEnabled ? 'Yes' : 'No'}</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Chop Policy: {plan.entryPolicy?.chopPolicy || 'default'}</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Gate Profile: {plan.entryPolicy?.scalpGateProfile || 'balanced'}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">Risk Policy</Typography>
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Leverage Cap: {plan.riskPolicy?.leverageCap || '—'}x</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Size Factor: {plan.riskPolicy?.positionSizeFactor || 1}x</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Cooldown: {plan.riskPolicy?.cooldownMinutes || '—'}m</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">Position Mgmt</Typography>
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Adaptive Trail: {plan.positionManagementPolicy?.adaptiveProfitTrailEnabled ? 'On' : 'Off'}</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>AI Close Guard: {plan.positionManagementPolicy?.aiCloseGuardEnabled ? 'On' : 'Off'}</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Zero Lock: {plan.positionManagementPolicy?.zeroLockMeaningfulEnabled ? 'On' : 'Off'}</Typography>
            </Box>
          </Grid>
        </Grid>
        {plan.reporting?.reasonCodes?.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">Reason Codes</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
              {plan.reporting.reasonCodes.map((code, i) => (
                <Chip key={i} label={code} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
              ))}
            </Box>
          </Box>
        )}
      </Collapse>
    </Paper>
  );
}

// ============================================================================
// MODE PERFORMANCE TABLE
// ============================================================================
function ModePerformanceTable({ modeMetrics, modeStatsFromDb, bestMode }) {
  const theme = useTheme();
  const allModes = [...(modeMetrics || [])];

  // Merge DB stats if available
  if (modeStatsFromDb?.length > 0) {
    for (const dbStat of modeStatsFromDb) {
      const existing = allModes.find(m => m.modeId === dbStat.ai_mode_id);
      if (existing) {
        existing.dbTrades = dbStat.trades;
        existing.dbPnlPct = dbStat.totalPnlPct;
        existing.dbAvgPnl = dbStat.avgPnlPct;
        existing.dbAvgDuration = dbStat.avgDurationMin;
      } else {
        allModes.push({
          modeId: dbStat.ai_mode_id,
          trades: dbStat.trades,
          wins: dbStat.wins,
          pnl: dbStat.totalPnlPct || 0,
          winRate: dbStat.trades > 0 ? ((dbStat.wins / dbStat.trades) * 100) : 0,
          dbTrades: dbStat.trades,
          dbPnlPct: dbStat.totalPnlPct,
          dbAvgPnl: dbStat.avgPnlPct,
          dbAvgDuration: dbStat.avgDurationMin,
        });
      }
    }
  }

  if (allModes.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
        <CompareArrowsIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">No mode performance data yet.</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <CompareArrowsIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="subtitle2" fontWeight="bold">Mode Performance Comparison</Typography>
        {bestMode && (
          <Chip label={`Best: ${bestMode.modeId}`} size="small" color="success" sx={{ fontSize: '0.65rem', height: 20, ml: 'auto' }} />
        )}
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontSize: '0.7rem', fontWeight: 'bold', color: 'text.secondary', py: 0.75 } }}>
              <TableCell>Mode</TableCell>
              <TableCell align="right">Trades</TableCell>
              <TableCell align="right">Wins</TableCell>
              <TableCell align="right">Win Rate</TableCell>
              <TableCell align="right">Total PnL</TableCell>
              <TableCell align="right">Avg PnL</TableCell>
              <TableCell align="right">Avg Duration</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allModes.sort((a, b) => (b.pnl || 0) - (a.pnl || 0)).map((m, i) => (
              <TableRow
                key={m.modeId || i}
                sx={{
                  bgcolor: bestMode?.modeId === m.modeId ? alpha(theme.palette.success.main, 0.05) : 'transparent',
                  '& td': { fontSize: '0.75rem', py: 0.5 },
                }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: bestMode?.modeId === m.modeId ? 'bold' : 'normal' }}>
                      {m.modeId}
                    </Typography>
                    {bestMode?.modeId === m.modeId && (
                      <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">{m.dbTrades || m.trades || 0}</TableCell>
                <TableCell align="right">{m.wins || 0}</TableCell>
                <TableCell align="right">
                  <Chip
                    label={`${(m.winRate || 0).toFixed(1)}%`}
                    size="small"
                    sx={{
                      fontSize: '0.65rem', height: 18,
                      bgcolor: (m.winRate || 0) >= 50 ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                      color: (m.winRate || 0) >= 50 ? 'success.main' : 'error.main',
                    }}
                  />
                </TableCell>
                <TableCell align="right"><PnlChip value={m.dbPnlPct || m.pnl} /></TableCell>
                <TableCell align="right">{m.dbAvgPnl ? `${Number(m.dbAvgPnl).toFixed(3)}%` : '—'}</TableCell>
                <TableCell align="right">{m.dbAvgDuration ? `${Number(m.dbAvgDuration).toFixed(0)}m` : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

// ============================================================================
// REGIME STATS CARD
// ============================================================================
function RegimeStatsCard({ regimeStats }) {
  const theme = useTheme();
  if (!regimeStats || regimeStats.length === 0) return null;

  const regimeColors = { trending: 'success', choppy: 'error', ranging: 'warning', unknown: 'default' };

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <TimelineIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="subtitle2" fontWeight="bold">Performance by Regime</Typography>
      </Box>
      <Grid container spacing={1.5}>
        {regimeStats.map((r, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, textAlign: 'center' }}>
              <Chip label={r.regime || 'unknown'} size="small" color={regimeColors[r.regime] || 'default'} sx={{ fontSize: '0.65rem', height: 20, mb: 1 }} />
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>{r.trades}</Typography>
              <Typography variant="caption" color="text.secondary">trades</Typography>
              <Box sx={{ mt: 0.5 }}>
                <PnlChip value={r.totalPnlPct} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                WR: {r.trades > 0 ? ((r.wins / r.trades) * 100).toFixed(1) : 0}%
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}

// ============================================================================
// RECENT AI TRADES TABLE
// ============================================================================
function RecentAITradesTable({ trades }) {
  const theme = useTheme();
  const [showAll, setShowAll] = useState(false);

  if (!trades || trades.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
        <HistoryIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">No AI-attributed trades yet.</Typography>
      </Paper>
    );
  }

  const displayTrades = showAll ? trades : trades.slice(0, 15);

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <HistoryIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="subtitle2" fontWeight="bold">Recent AI-Attributed Trades</Typography>
        <Chip label={`${trades.length} trades`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18, ml: 'auto' }} />
      </Box>
      <TableContainer sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ '& th': { fontSize: '0.7rem', fontWeight: 'bold', color: 'text.secondary', py: 0.75, bgcolor: 'background.paper' } }}>
              <TableCell>Symbol</TableCell>
              <TableCell>Side</TableCell>
              <TableCell>AI Mode</TableCell>
              <TableCell>Regime</TableCell>
              <TableCell align="right">PnL</TableCell>
              <TableCell>Close Reason</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayTrades.map((t, i) => (
              <TableRow key={i} sx={{ '& td': { fontSize: '0.75rem', py: 0.5 } }}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{t.symbol}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={t.side}
                    size="small"
                    sx={{
                      fontSize: '0.6rem', height: 18,
                      bgcolor: t.side === 'buy' ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                      color: t.side === 'buy' ? 'success.main' : 'error.main',
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: '0.7rem' }}>{t.aiAttribution?.modeId || t.modeId || '—'}</TableCell>
                <TableCell>
                  <Chip label={t.aiAttribution?.regime || '—'} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 16 }} />
                </TableCell>
                <TableCell align="right"><PnlChip value={t.pnlPct} /></TableCell>
                <TableCell sx={{ fontSize: '0.7rem' }}>{t.closeReason || '—'}</TableCell>
                <TableCell sx={{ fontSize: '0.7rem' }}>{t.durationMin ? `${Number(t.durationMin).toFixed(0)}m` : '—'}</TableCell>
                <TableCell sx={{ fontSize: '0.7rem' }}>
                  {t.openedAt ? new Date(t.openedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {trades.length > 15 && (
        <Box sx={{ p: 1, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
          <Button size="small" onClick={() => setShowAll(!showAll)} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
            {showAll ? 'Show Less' : `Show All (${trades.length})`}
          </Button>
        </Box>
      )}
    </Paper>
  );
}

// ============================================================================
// ROTATION STATUS CARD
// ============================================================================
function RotationCard({ rotation }) {
  const theme = useTheme();
  if (!rotation) return null;

  const progress = rotation.totalModes > 0
    ? ((rotation.testedModes?.length || 0) / rotation.totalModes) * 100
    : 0;

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <ScienceIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="subtitle2" fontWeight="bold">Rotation Progress</Typography>
        <Chip label={`Cycle ${rotation.cycleCount || 0}`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18, ml: 'auto' }} />
      </Box>
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {rotation.testedModes?.length || 0} / {rotation.totalModes || 0} modes tested
          </Typography>
          <Typography variant="caption" fontWeight="bold">{progress.toFixed(0)}%</Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 6, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1) }}
        />
      </Box>
      {rotation.testedModes?.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
          {rotation.testedModes.map((modeId, i) => (
            <Chip key={i} label={modeId} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
          ))}
        </Box>
      )}
    </Paper>
  );
}

// ============================================================================
// AUDIT LOG CARD
// ============================================================================
function AuditLogCard({ auditEntries }) {
  const [expanded, setExpanded] = useState(false);

  if (!auditEntries || auditEntries.length === 0) return null;

  const displayEntries = expanded ? auditEntries.slice(-20).reverse() : auditEntries.slice(-5).reverse();

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <SecurityIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="subtitle2" fontWeight="bold">AI Schedule Audit Log</Typography>
        <Chip label={`${auditEntries.length} entries`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18, ml: 'auto' }} />
        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Box sx={{ maxHeight: expanded ? 400 : 200, overflow: 'auto' }}>
        {displayEntries.map((entry, i) => (
          <Box
            key={i}
            sx={{
              px: 1.5, py: 0.75,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:last-child': { borderBottom: 'none' },
              display: 'flex', alignItems: 'center', gap: 1,
            }}
          >
            <Chip label={entry.action} size="small" color={entry.action === 'apply' ? 'success' : 'info'} sx={{ fontSize: '0.6rem', height: 18, minWidth: 50 }} />
            <Typography variant="body2" sx={{ fontSize: '0.7rem', flex: 1 }}>
              Mode: <strong>{entry.modeId}</strong> | Regime: <strong>{entry.regime}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {entry.timestamp ? new Date(entry.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

// ============================================================================
// MAIN: AIExperimentsTab
// ============================================================================
export default function AIExperimentsTab({ strategyId, scheduleData }) {
  const theme = useTheme();
  const [report, setReport] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningDaily, setRunningDaily] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const fetchReport = useCallback(async () => {
    if (!strategyId) return;
    try {
      setLoading(true);
      setError(null);

      const [reportRes, auditRes] = await Promise.all([
        fetch(`${STRATEGY_ENGINE_URL}/api/admin/ai-schedule/${strategyId}/report`, { headers: headers() }),
        fetch(`${STRATEGY_ENGINE_URL}/api/admin/ai-schedule/${strategyId}/audit`, { headers: headers() }),
      ]);

      if (reportRes.ok) {
        const reportData = await reportRes.json();
        if (reportData.success) setReport(reportData.data);
        else setError(reportData.error || 'Failed to load report');
      } else if (reportRes.status === 404) {
        setReport(null);
      }

      if (auditRes.ok) {
        const auditData = await auditRes.json();
        if (auditData.success) setAuditLog(auditData.data?.entries || []);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch AI schedule data');
    } finally {
      setLoading(false);
    }
  }, [strategyId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleRunDaily = useCallback(async () => {
    if (!strategyId) return;
    try {
      setRunningDaily(true);
      setError(null);
      const res = await fetch(`${STRATEGY_ENGINE_URL}/api/admin/ai-schedule/run-daily-update`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ strategyId }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccessMsg(`Daily update complete: ${data.data?.selectedMode} (${data.data?.regime?.label})`);
        setTimeout(() => setSuccessMsg(null), 5000);
        await fetchReport();
      } else {
        setError(data.error || 'Daily update failed');
      }
    } catch (err) {
      setError(err.message || 'Daily update request failed');
    } finally {
      setRunningDaily(false);
    }
  }, [strategyId, fetchReport]);

  if (!strategyId) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">Select a strategy to view AI experiments.</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Loading AI schedule data...</Typography>
      </Box>
    );
  }

  if (!report) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <SmartToyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>No AI Schedule Configured</Typography>
        <Typography variant="body2" color="text.secondary">
          Add an AI Schedule Node to your strategy in the Strategy Builder, or enable AI mode in the schedule settings.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert severity="success" onClose={() => setSuccessMsg(null)}>{successMsg}</Alert>}

      {/* Header with refresh */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SmartToyIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6" fontWeight="bold">AI Schedule Experiments</Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchReport} size="small">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* AI Config Status */}
      <AIConfigCard config={report.config} onRunDaily={handleRunDaily} running={runningDaily} />

      {/* Current Policy Plan */}
      <PolicyPlanCard plan={report.currentPlan} />

      {/* Rotation Progress */}
      <RotationCard rotation={report.rotation} />

      {/* Mode Performance Comparison */}
      <ModePerformanceTable
        modeMetrics={report.modeMetrics}
        modeStatsFromDb={report.modeStatsFromDb}
        bestMode={report.bestMode}
      />

      {/* Regime Stats */}
      <RegimeStatsCard regimeStats={report.regimeStats} />

      {/* Recent AI Trades */}
      <RecentAITradesTable trades={report.recentAITrades} />

      {/* Audit Log */}
      <AuditLogCard auditEntries={auditLog} />
    </Box>
  );
}
